import Box from '@mui/material/Box';
import { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { AddOnRateType } from '@treadinc/horizon-api-spec';
import { Trans } from 'react-i18next';

import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import { toRateNumber } from '~utils/rateFormatting';

interface InstallmentsHelperTextProps {
  rateType?: string;
  rateValue?: number | string;
  quantity?: number | string;
  totalInstallments?: number | string;
}

const valueSx = (theme: Theme) => ({
  color: theme.brandV2.colors.treadGray1,
  fontWeight: 700,
});
const mathSx = (theme: Theme) => ({ color: theme.brandV2.colors.treadGray3 });
const textSx = (theme: Theme) => ({
  color: theme.brandV2.colors.treadGray2,
  fontSize: 14,
  mt: 0.5,
});

const splitHintKey = (isDeduction: boolean, isSingleQuantity: boolean) => {
  const direction = isDeduction ? 'deducted' : 'added';
  const scale = isSingleQuantity ? '' : '_scaled';

  return `administration.rates.automatic_add_ons.installments_split_${direction}${scale}_hint`;
};

const percentHintKey = (isDeduction: boolean) =>
  `administration.rates.automatic_add_ons.installments_percent_${
    isDeduction ? 'deducted' : 'added'
  }_hint`;

// The backend applies a percentage in full every settlement and only caps how many
// it runs on, so that case has no total to divide.
const InstallmentsHelperText = ({
  rateType,
  rateValue,
  quantity,
  totalInstallments,
}: InstallmentsHelperTextProps) => {
  const { currencyFormatter } = useCompanyCurrency();

  const installments = toRateNumber(totalInstallments) ?? 0;
  const rate = toRateNumber(rateValue);
  const isPercentRate = rateType === AddOnRateType.RATE_PERCENT_OF_TOTAL;
  const isSplitRate =
    rateType === AddOnRateType.RATE_FOR_EACH || rateType === AddOnRateType.PER_LOAD;

  if (!isSplitRate && !isPercentRate) {
    return null;
  }

  const isUsableCount = installments > 0 && Number.isInteger(installments);

  if (!isUsableCount || rate === null) {
    return null;
  }

  if (isPercentRate) {
    return (
      <Typography variant="body2" sx={textSx}>
        <Trans
          components={{ value: <Box component="span" sx={valueSx} /> }}
          i18nKey={percentHintKey(rate < 0)}
          values={{ installments, percentage: Math.abs(rate) }}
        />
      </Typography>
    );
  }

  const defaultQuantity = toRateNumber(quantity) ?? 1;
  const perSettlement = (rate / installments) * defaultQuantity;
  const isDeduction = perSettlement < 0;

  return (
    <Typography variant="body2" sx={textSx}>
      <Trans
        components={{
          math: <Box component="span" sx={mathSx} />,
          value: <Box component="span" sx={valueSx} />,
        }}
        i18nKey={splitHintKey(isDeduction, defaultQuantity === 1)}
        values={{
          installments,
          perSettlement: currencyFormatter(Math.abs(perSettlement)),
          quantity: defaultQuantity,
          total: currencyFormatter(Math.abs(rate)),
        }}
      />
    </Typography>
  );
};

export default InstallmentsHelperText;
